import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleAlert, Download, FileWarning, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { CreateBolDialog } from "@/components/bols/create-bol-dialog";
import { StatusBadge } from "@/components/bols/status-badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api";
import { saveFile } from "@/lib/download";
import {
  bolQuery,
  bolsApi,
  bolsQuery,
  bolSummaryQuery,
  STATUS_LABEL,
  type BolListItem,
  type BolStatus,
} from "@/lib/bols";
import { cn } from "@/lib/utils";

const searchSchema = z.object({
  search: z.string().optional(),
  status: z.enum(["draft", "extracting", "review", "ready", "exported"]).optional(),
  attention: z.boolean().optional(),
  page: z.number().int().min(1).optional(),
});

export const Route = createFileRoute("/_authenticated/bols/")({
  validateSearch: searchSchema,
  component: BolsPage,
});

const STATUS_ORDER: BolStatus[] = ["draft", "extracting", "review", "ready", "exported"];

function BolsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<BolListItem | null>(null);
  const [draft, setDraft] = useState(search.search ?? "");
  const list = useQuery(
    bolsQuery({
      search: search.search,
      status: search.status,
      attention: search.attention,
      page: search.page,
    }),
  );
  const summary = useQuery(bolSummaryQuery);

  const rows = list.data?.results ?? [];
  const count = list.data?.count ?? 0;
  const page = search.page ?? 1;
  const pageSize = 50;
  const lastPage = Math.max(1, Math.ceil(count / pageSize));

  const filter = (patch: Partial<typeof search>) =>
    navigate({ search: { ...search, ...patch, page: undefined } });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <h1 className="text-base font-semibold">
          Bills of lading
          <span className="text-muted-foreground font-normal">
            {summary.data ? ` · ${summary.data.total} in total` : ""}
          </span>
        </h1>
        <Button size="sm" className="ml-auto" onClick={() => setCreating(true)}>
          New bill of lading
        </Button>
      </div>

      <section className="bg-background rounded-md border">
        <header className="flex flex-wrap items-center gap-3 border-b px-3 py-2">
          <h2 className="text-xs font-semibold tracking-wide uppercase">Work queue</h2>
          <form
            className="ml-auto flex flex-wrap gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void filter({ search: draft || undefined });
            }}
          >
            <Input
              placeholder="Search B/L number, manifest, description, consignee…"
              className="h-8 w-80"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <Button type="submit" size="sm" variant="outline">
              Search
            </Button>
            {search.search && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft("");
                  void filter({ search: undefined });
                }}
              >
                Clear
              </Button>
            )}
          </form>
        </header>

        {/* work queue: one chip per status, plus the ones that need a person */}
        <div className="flex flex-wrap gap-2 border-b px-3 py-2">
          <Chip
            active={!search.status && !search.attention}
            onClick={() => filter({ status: undefined, attention: undefined })}
          >
            All <Count n={summary.data?.total} />
          </Chip>
          <Chip
            active={!!search.attention}
            tone="warn"
            onClick={() =>
              filter({ status: undefined, attention: search.attention ? undefined : true })
            }
          >
            <CircleAlert className="size-3.5" /> Needs attention{" "}
            <Count n={summary.data?.attention} />
          </Chip>
          {STATUS_ORDER.map((s) => (
            <Chip
              key={s}
              active={search.status === s}
              onClick={() =>
                filter({ status: search.status === s ? undefined : s, attention: undefined })
              }
            >
              {STATUS_LABEL[s]} <Count n={summary.data?.by_status[s]} />
            </Chip>
          ))}
        </div>

        <Table>
          <TableHeader>
            <TableRow className="text-muted-foreground text-[10px] uppercase">
              <TableHead className="h-8 font-medium">Bill of lading</TableHead>
              <TableHead className="h-8 font-medium">Consignee</TableHead>
              <TableHead className="h-8 font-medium">Status</TableHead>
              <TableHead className="h-8 text-right font-medium">Invoices</TableHead>
              <TableHead className="h-8 text-right font-medium">Items</TableHead>
              <TableHead className="h-8 font-medium">Updated</TableHead>
              <TableHead className="h-8 font-medium">By</TableHead>
              <TableHead className="h-8 w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-muted-foreground py-10 text-center">
                  {list.isLoading ? (
                    "Loading…"
                  ) : search.attention ? (
                    "Nothing needs attention."
                  ) : search.search || search.status ? (
                    "No bills of lading match."
                  ) : (
                    <span>
                      No bills of lading yet.{" "}
                      <button className="text-primary underline" onClick={() => setCreating(true)}>
                        Create the first one
                      </button>
                      .
                    </span>
                  )}
                </TableCell>
              </TableRow>
            )}
            {rows.map((bol) => (
              <BolRow key={bol.id} bol={bol} onDelete={() => setDeleting(bol)} />
            ))}
          </TableBody>
        </Table>
      </section>

      {lastPage > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => navigate({ search: { ...search, page: page - 1 } })}
          >
            Previous
          </Button>
          <span>
            Page {page} of {lastPage}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= lastPage}
            onClick={() => navigate({ search: { ...search, page: page + 1 } })}
          >
            Next
          </Button>
        </div>
      )}

      <CreateBolDialog open={creating} onOpenChange={setCreating} />
      <DeleteBolDialog bol={deleting} onClose={() => setDeleting(null)} />
    </div>
  );
}

function BolRow({ bol, onDelete }: { bol: BolListItem; onDelete: () => void }) {
  const navigate = useNavigate();
  const download = useMutation({
    mutationFn: () => bolsApi.downloadUploads(bol.id),
    onSuccess: saveFile,
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : "Could not download the files"),
  });
  const open = () => void navigate({ to: "/bols/$bolId", params: { bolId: bol.id } });
  const failed = bol.failed_invoices > 0 || (!!bol.extraction_error && bol.status !== "extracting");
  return (
    <TableRow
      className="cursor-pointer"
      onClick={open}
      onKeyDown={(e) => e.key === "Enter" && open()}
      tabIndex={0}
    >
      <TableCell className="font-mono text-[13px]">
        <div>
          {bol.manifest_reference && (
            <span className="text-muted-foreground">{bol.manifest_reference}/</span>
          )}
          {bol.reference || (
            <span className="text-muted-foreground font-sans italic">no number yet</span>
          )}
        </div>
        {bol.description && (
          <div className="text-muted-foreground font-sans text-xs">{bol.description}</div>
        )}
      </TableCell>
      <TableCell>{bol.consignee_name ?? "—"}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          {bol.status === "extracting" && !bol.extraction_is_stale && (
            <Loader2 className="text-muted-foreground size-3.5 animate-spin" />
          )}
          <StatusBadge status={bol.status} />
        </div>
        {bol.attention.length > 0 && (
          <ul className="mt-1 space-y-0.5 text-xs">
            {bol.attention.map((reason) => (
              <li
                key={reason}
                className={cn(
                  "flex items-start gap-1",
                  failed || reason.startsWith("Reading seems stuck")
                    ? "text-destructive"
                    : "text-amber-700",
                )}
              >
                {failed ? (
                  <FileWarning className="mt-0.5 size-3 shrink-0" />
                ) : (
                  <CircleAlert className="mt-0.5 size-3 shrink-0" />
                )}
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        )}
        {bol.status === "exported" && bol.exported_at && (
          <div className="text-muted-foreground mt-1 text-xs">
            Exported {new Date(bol.exported_at).toLocaleDateString()}
          </div>
        )}
      </TableCell>
      <TableCell className="text-right font-mono text-[13px]">{bol.invoice_count}</TableCell>
      <TableCell className="text-right font-mono text-[13px]">{bol.post_count}</TableCell>
      <TableCell title={new Date(bol.updated_at).toLocaleString()}>
        {relative(bol.updated_at)}
      </TableCell>
      <TableCell className="text-muted-foreground">{bol.created_by_email}</TableCell>
      <TableCell className="py-1 whitespace-nowrap">
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground size-8"
          title="Download the uploaded files (one PDF, or a zip when there are several)"
          aria-label={`Download the files of ${bol.reference}`.trim()}
          disabled={download.isPending}
          onClick={(e) => {
            e.stopPropagation(); // the row itself opens the bill
            download.mutate();
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {download.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Download className="size-4" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-destructive size-8"
          title="Delete this bill with its files, invoices and items"
          aria-label={`Delete bill of lading ${bol.reference}`.trim()}
          onClick={(e) => {
            e.stopPropagation(); // the row itself opens the bill
            onDelete();
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Trash2 className="size-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function DeleteBolDialog({ bol, onClose }: { bol: BolListItem | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: (target: BolListItem) => bolsApi.deleteBol(target.id),
    onSuccess: async (_, target) => {
      queryClient.removeQueries({ queryKey: bolQuery(target.id).queryKey });
      await queryClient.invalidateQueries({ queryKey: ["bols"], exact: false });
      toast.success(`Bill of lading ${target.reference || ""} deleted`.trim());
      onClose();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Could not delete"),
  });
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
  return (
    <AlertDialog open={!!bol} onOpenChange={(open) => !open && !remove.isPending && onClose()}>
      <AlertDialogContent>
        {bol && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete bill of lading {bol.reference || ""}?</AlertDialogTitle>
              <AlertDialogDescription>
                The uploaded files, {plural(bol.invoice_count, "invoice")} with their lines,{" "}
                {plural(bol.post_count, "item")}
                {bol.status === "exported" ? ", and the exported XML" : ""} are removed for good.
                This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive hover:bg-destructive/90 text-white"
                disabled={remove.isPending}
                onClick={(e) => {
                  e.preventDefault();
                  remove.mutate(bol);
                }}
              >
                {remove.isPending ? "Deleting…" : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Chip({
  active,
  tone,
  onClick,
  children,
}: {
  active: boolean;
  tone?: "warn";
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-xs",
        active ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted",
        tone === "warn" && !active && "border-amber-300 text-amber-800",
      )}
    >
      {children}
    </button>
  );
}

function Count({ n }: { n: number | undefined }) {
  if (n === undefined) return null;
  return <span className="rounded bg-black/10 px-1.5 font-mono text-[11px]">{n}</span>;
}

/** "3 min ago", "yesterday", or the date for older rows. */
function relative(iso: string): string {
  const then = new Date(iso).getTime();
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}
